import { useCallback } from 'react'

import { Editor, useEditorState } from '@tiptap/react'
import { LuHeading1, LuHeading2, LuHeading3 } from 'react-icons/lu'
import { TbList, TbListNumbers, TbPilcrow } from 'react-icons/tb'

import {
  isCustomNodeSelected,
  isTextSelected,
} from '@/components/common/TextArea/RichTextArea/menus/TextMenu/lib'
import type {
  ContentPickerOptions,
  ShouldShowProps,
} from '@/components/common/TextArea/RichTextArea/menus/TextMenu/types'

import type { UUID } from '@/types/Id'

interface UseTextMenuCommandsProps {
  editor: Editor | null
  onApproveCommentsCallback?: (ids: UUID[]) => void
}

export const useTextMenuCommands = (props: UseTextMenuCommandsProps) => {
  const editor = props?.editor ?? null
  const onApproveCommentsCallback =
    props?.onApproveCommentsCallback ?? undefined

  const onBold = useCallback(
    () => editor?.chain().focus().toggleBold().run(),
    [editor],
  )
  const onItalic = useCallback(
    () => editor?.chain().focus().toggleItalic().run(),
    [editor],
  )
  const onStrike = useCallback(
    () => editor?.chain().focus().toggleStrike().run(),
    [editor],
  )
  const onUnderline = useCallback(
    () => editor?.chain().focus().toggleUnderline().run(),
    [editor],
  )
  const onCodeBlock = useCallback(
    () => editor?.chain().focus().toggleCodeBlock().run(),
    [editor],
  )

  const onBulletList = useCallback(
    () => editor?.chain().focus().toggleBulletList().run(),
    [editor],
  )

  const onOrderedList = useCallback(
    () => editor?.chain().focus().toggleOrderedList().run(),
    [editor],
  )

  const onSubscript = useCallback(
    () => editor?.chain().focus().toggleSubscript().run(),
    [editor],
  )
  const onSuperscript = useCallback(
    () => editor?.chain().focus().toggleSuperscript().run(),
    [editor],
  )
  const onAlignLeft = useCallback(
    () => editor?.chain().focus().setTextAlign('left').run(),
    [editor],
  )
  const onAlignCenter = useCallback(
    () => editor?.chain().focus().setTextAlign('center').run(),
    [editor],
  )
  const onAlignRight = useCallback(
    () => editor?.chain().focus().setTextAlign('right').run(),
    [editor],
  )
  const onAlignJustify = useCallback(
    () => editor?.chain().focus().setTextAlign('justify').run(),
    [editor],
  )

  const onChangeHighlight = useCallback(
    (color: string) => editor?.chain().setHighlight({ color }).run(),
    [editor],
  )
  const onClearHighlight = useCallback(
    () => editor?.chain().focus().unsetHighlight().run(),
    [editor],
  )

  const onSetLink = useCallback(
    (url: string, inNewTab?: boolean) =>
      editor
        ?.chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: url, target: inNewTab ? '_blank' : '' })
        .run(),
    [editor],
  )

  const onUnsetLink = useCallback(
    () => editor?.chain().focus().unsetLink().run(),
    [editor],
  )

  const onApproveComment = useCallback(() => {
    if (!editor) return

    const { $from, $to } = editor.state.selection
    const commentMarks =
      $from
        .marksAcross($to)
        ?.filter(
          (mark) => mark.type.name === 'comment' && mark.attrs.needsReview,
        ) || []

    const commentIds = commentMarks.map((mark) => mark.attrs.id)

    commentIds.forEach((id) => {
      if (id) {
        editor.chain().focus().resolveComment({ id }).run()
      }
    })

    onApproveCommentsCallback?.(commentIds)
  }, [editor, onApproveCommentsCallback])

  const onUndo = useCallback(() => {
    if (!editor) return
    editor.chain().focus().undo().run()
  }, [editor])

  const onRedo = useCallback(() => {
    if (!editor) return
    editor.chain().focus().redo().run()
  }, [editor])

  return {
    onApproveComment,
    onUndo,
    onRedo,
    onBold,
    onItalic,
    onStrike,
    onUnderline,
    onCodeBlock,
    onBulletList,
    onOrderedList,
    onSubscript,
    onSuperscript,
    onAlignLeft,
    onAlignCenter,
    onAlignRight,
    onAlignJustify,
    onChangeHighlight,
    onClearHighlight,
    onSetLink,
    onUnsetLink,
  }
}

interface UseTextMenuDisabledStateProps {
  editor: Editor | null
}

export const useTextMenuDisabledState = (
  props: UseTextMenuDisabledStateProps,
) => {
  const editor = props?.editor ?? null
  const editable = editor?.options.editable
  return {
    canBold: !editor?.can().toggleBold() || !editable,
    canItalic: !editor?.can().toggleItalic() || !editable,
    canStrike: !editor?.can().toggleStrike() || !editable,
    canUnderline: !editor?.can().toggleUnderline() || !editable,
    canCode: !editor?.can().toggleCodeBlock() || !editable,
    canBulletList: !editor?.can().toggleBulletList() || !editable,
    canOrderedList: !editor?.can().toggleOrderedList() || !editable,
    canSubscript: !editor?.can().toggleSubscript() || !editable,
    canSuperscript: !editor?.can().toggleSuperscript() || !editable,
    canAlignLeft: !editor?.can().setTextAlign('left') || !editable,
    canAlignCenter: !editor?.can().setTextAlign('center') || !editable,
    canAlignRight: !editor?.can().setTextAlign('right') || !editable,
    canAlignJustify: !editor?.can().setTextAlign('justify') || !editable,
    canSetLink: !editor?.can().setLink({ href: '', target: '' }) || !editable,
    canUnsetLink: !editor?.isActive('link') || !editable,
    canSetHighlight: !editor?.can().setHighlight({ color: '' }) || !editable,
    canApproveComment:
      !editor?.isActive('comment', { needsReview: true }) || !editable,
    canUndo: !editor?.can().undo() || !editable,
    canRedo: !editor?.can().redo() || !editable,
  }
}

interface UseTextMenuEditableStateProps {
  editor: Editor | null
}

export const useTextMenuEditableState = (
  props: UseTextMenuEditableStateProps,
) => {
  const editor = props?.editor ?? null
  return {
    editable: editor?.options.editable,
  }
}

interface UseTextMenuContentTypesProps {
  editor: Editor | null
}

export const useTextMenuContentTypes = (
  props: UseTextMenuContentTypesProps,
) => {
  const editor = props?.editor ?? null
  return useEditorState({
    editor,
    selector: (ctx): ContentPickerOptions => [
      {
        type: 'category',
        label: 'Hierarchy',
        id: 'hierarchy',
      },
      {
        icon: TbPilcrow,
        onClick: () =>
          ctx.editor
            ?.chain()
            .focus()
            .liftListItem('listItem')
            .setParagraph()
            .run(),
        id: 'paragraph',
        disabled: () => !ctx.editor?.can().setParagraph(),
        isActive: () =>
          (ctx.editor?.isActive('paragraph') &&
            !ctx.editor?.isActive('orderedList') &&
            !ctx.editor?.isActive('bulletList') &&
            !ctx.editor?.isActive('taskList')) ??
          false,
        label: 'Paragraph',
        type: 'option',
      },
      {
        icon: LuHeading1,
        onClick: () =>
          ctx.editor
            ?.chain()
            .focus()
            .liftListItem('listItem')
            .setHeading({ level: 1 })
            .run(),
        id: 'heading1',
        disabled: () => !ctx.editor?.can().setHeading({ level: 1 }),
        isActive: () => ctx.editor?.isActive('heading', { level: 1 }) ?? false,
        label: 'Heading 1',
        type: 'option',
      },
      {
        icon: LuHeading2,
        onClick: () =>
          ctx.editor
            ?.chain()
            .focus()
            .liftListItem('listItem')
            .setHeading({ level: 2 })
            .run(),
        id: 'heading2',
        disabled: () => !ctx.editor?.can().setHeading({ level: 2 }),
        isActive: () => ctx.editor?.isActive('heading', { level: 2 }) ?? false,
        label: 'Heading 2',
        type: 'option',
      },
      {
        icon: LuHeading3,
        onClick: () =>
          ctx.editor
            ?.chain()
            .focus()
            .liftListItem('listItem')
            .setHeading({ level: 3 })
            .run(),
        id: 'heading3',
        disabled: () => !editor?.can().setHeading({ level: 3 }),
        isActive: () => editor?.isActive('heading', { level: 3 }) ?? false,
        label: 'Heading 3',
        type: 'option',
      },
      {
        type: 'category',
        label: 'Lists',
        id: 'lists',
      },
      {
        icon: TbList,
        onClick: () => ctx.editor?.chain().focus().toggleBulletList().run(),
        id: 'bulletList',
        disabled: () => !ctx.editor?.can().toggleBulletList(),
        isActive: () => ctx.editor?.isActive('bulletList') ?? false,
        label: 'Bulleted list',
        type: 'option',
      },
      {
        icon: TbListNumbers,
        onClick: () => ctx.editor?.chain().focus().toggleOrderedList().run(),
        id: 'orderedList',
        disabled: () => !ctx.editor?.can().toggleOrderedList(),
        isActive: () => ctx.editor?.isActive('orderedList') ?? false,
        label: 'Numbered list',
        type: 'option',
      },
    ],
  })
}

interface UseTextMenuStateProps {
  editor: Editor | null
}

export const useTextMenuState = (props: UseTextMenuStateProps) => {
  const editor = props.editor
  const states = useEditorState({
    editor,
    selector: (ctx) => {
      return {
        isBold: ctx.editor?.isActive('bold') ?? false,
        isItalic: ctx.editor?.isActive('italic') ?? false,
        isStrike: ctx.editor?.isActive('strike') ?? false,
        isUnderline: ctx.editor?.isActive('underline') ?? false,
        isCode: ctx.editor?.isActive('code') ?? false,
        isSubscript: ctx.editor?.isActive('subscript') ?? false,
        isSuperscript: ctx.editor?.isActive('superscript') ?? false,
        isAlignLeft: ctx.editor?.isActive({ textAlign: 'left' }) ?? false,
        isAlignCenter: ctx.editor?.isActive({ textAlign: 'center' }) ?? false,
        isAlignRight: ctx.editor?.isActive({ textAlign: 'right' }) ?? false,
        isAlignJustify: ctx.editor?.isActive({ textAlign: 'justify' }) ?? false,
        isBulletList: ctx.editor?.isActive('bulletList') ?? false,
        isOrderedList: ctx.editor?.isActive('orderedList') ?? false,
        isHighlight: ctx.editor?.isActive('highlight') ?? false,
        canApprove: editor && editor.isActive('comment', { needsReview: true }),
        currentColor:
          ctx.editor?.getAttributes('textStyle')?.color || undefined,
        currentHighlight:
          ctx.editor?.getAttributes('highlight')?.color || undefined,
        currentFont:
          ctx.editor?.getAttributes('textStyle')?.fontFamily || undefined,
        currentSize:
          ctx.editor?.getAttributes('textStyle')?.fontSize || undefined,
      }
    },
  })

  const shouldShow = useCallback(
    ({ view, from }: ShouldShowProps) => {
      if (!view || !editor || editor.view.dragging) {
        return false
      }

      const domAtPos = view.domAtPos(from || 0)
      if (!domAtPos || !domAtPos.node) {
        return false
      }
      const nodeDOM = view.nodeDOM(from || 0) as HTMLElement
      const node = nodeDOM || domAtPos.node

      if (isCustomNodeSelected(editor, node)) {
        return false
      }

      const textSelected = isTextSelected({ editor })
      return textSelected
    },
    [editor],
  )

  return {
    shouldShow,
    ...states,
  }
}
